import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType } from '@ngx-formly/material';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { cloneDeep, defaultsDeep, forEach, get, map, merge } from 'lodash';
import { FormArray, FormGroup } from '@angular/forms';
import { map as rxMap, Observable, Subject, takeUntil } from 'rxjs';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';

export interface ReverseTableRow {
  id: string;
  label: string;
  fields: FormlyFieldConfig[];
  color?: string;
}

interface Summary {
  label: string;
  addonLeft: string[];
  addonRight: string[];
  decimalFormat: string;
}

interface InputProps extends FormlyFieldProps {
  tableRows: ReverseTableRow[];
  tableColumns?: string[];
  commonRowFieldsConfig: Partial<FormlyFieldConfig>;
  summary: Summary;
  addonLeft: string;
  addonRight: string;
  clearInputOnFocus: boolean;
  readonly: boolean;
}

@Component({
    selector: 'lockton-apps-reverse-calc-table-type',
    templateUrl: './reverse-calc-table-type.component.html',
    styleUrls: ['./reverse-calc-table-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReverseCalcTableTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly destroyer$: Subject<void> = new Subject();
  internalForm!: FormArray;
  internalModel!: any;
  rows$!: Observable<ReverseTableRow[]>;

  constructor(private readonly castObservable: CastObservablePipe) {
    super();
  }

  private get formattedInternalFormValue(): unknown {
    return merge({}, ...this.internalForm.value);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.listenToExternalChanges();
    this.rows$ = this.castObservable
      .transform(this.props.tableRows)
      .pipe(rxMap(this.enhanceRows.bind(this)), takeUntil(this.destroyer$));
    this.internalModel = cloneDeep(get(this.model, this.key));
  }

  ngAfterViewInit(): void {
    this.onModelChange(true);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  onModelChange(initialUpdate?: boolean): void {
    this.formControl.setValue(this.formattedInternalFormValue);
    this.formControl.updateValueAndValidity();

    if (!initialUpdate) {
      this.formControl.markAsDirty();
    }
  }

  private initializeForm(): void {
    this.internalForm = new FormArray<any>([]);

    this.formControl.setValidators([
      () => (this.internalForm.invalid ? { invalidAncestors: true } : null),
    ]);
  }

  private listenToExternalChanges(): void {
    if (!this.options.formState.validityCheck$) {
      return;
    }
    this.options.formState.validityCheck$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.internalForm.markAllAsTouched();
        this.formControl.markAsDirty();
        this.formControl.updateValueAndValidity();
      });
  }

  private enhanceRows(rows: ReverseTableRow[]): ReverseTableRow[] {
    return map(rows, (row: ReverseTableRow) => {
      this.internalForm.push(new FormGroup({}));

      forEach(row.fields, (field: FormlyFieldConfig) => {
        if (!field.key) {
          return;
        }

        defaultsDeep(field, this.props.commonRowFieldsConfig, {
          id: `${this.key}.${field.key}`,
          props: {
            clearOnFocus: this.props.clearInputOnFocus,
            readonly: this.props.readonly,
          },
          defaultValue: get(
            this.formControl.defaultValue || this.field.defaultValue,
            field.key as string,
            0
          ),
        });
      });

      return row;
    });
  }
}
